/** Multi-note annotations stored in scan.notes (JSON array or legacy plain text). */

export type AnnotationNote = {
  id: string;
  text: string;
  tags: string[];
  createdAt: string;
};

function isNoteShape(v: unknown): v is AnnotationNote {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.text === "string";
}

export function parseAnnotationNotes(raw?: string | null, fallbackTags: string[] = []): AnnotationNote[] {
  const value = (raw ?? "").trim();
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter(isNoteShape).map((n, i) => ({
        id: typeof n.id === "string" && n.id ? n.id : `n-${i}`,
        text: n.text.trim(),
        tags: Array.isArray(n.tags) ? n.tags.filter((t): t is string => typeof t === "string") : [],
        createdAt:
          typeof n.createdAt === "string" && n.createdAt
            ? n.createdAt
            : new Date(0).toISOString(),
      })).filter((n) => n.text.length > 0 || n.tags.length > 0);
    }
  } catch {
    /* plain text legacy */
  }
  return [
    {
      id: "legacy",
      text: value,
      tags: fallbackTags,
      createdAt: new Date(0).toISOString(),
    },
  ];
}

export function serializeAnnotationNotes(notes: AnnotationNote[]): string {
  return JSON.stringify(
    notes.map((n) => ({
      id: n.id,
      text: n.text,
      tags: n.tags,
      createdAt: n.createdAt,
    })),
  );
}

export function newAnnotationNote(text: string, tags: string[] = []): AnnotationNote {
  return {
    id: `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    text: text.trim(),
    tags: [...tags],
    createdAt: new Date().toISOString(),
  };
}

/**
 * Human-readable preview for lists / CSV.
 * Never show raw JSON arrays like `[{"id":"n-…","text":"…"}]`.
 */
export function formatAnnotationNotesPreview(
  raw?: string | null,
  fallbackTags: string[] = [],
  separator = " · ",
): string {
  const notes = parseAnnotationNotes(raw, fallbackTags);
  return notes
    .map((n) => n.text.trim())
    .filter(Boolean)
    .join(separator);
}
